import { graphql } from 'react-relay';
import type { FC } from 'react';
import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import Button from '@atlaskit/button/custom-theme-button';
import { token } from '@atlaskit/tokens';
import AkSpinner from '@atlaskit/spinner/spinner';

import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { ExternalCollaboratorUserLozenge } from '@confluence/external-collab-ui/entry-points/ExternalUserLozengeLoader.ts';
import { GlobalPermissionsUserRow } from '@confluence/site-permissions/entry-points/GroupAndUserItem.ts';
import { PROFILELINK_CLASSNAME } from '@confluence/site-permissions/entry-points/constants.ts';

import type {
	SpaceExternalListScrollableSpaceExternalListQuery as SpaceExternalListScrollableSpaceExternalListQuery$data,
	SpaceExternalListScrollableSpaceExternalListQueryVariables as SpaceExternalListScrollableSpaceExternalListQuery$variables,
	SpaceExternalListScrollableSpaceExternalListQuery_space_Space_directAccessExternalCollaborators_PaginatedPersonList_nodes_KnownUser as KnownUser,
} from './__types__/SpaceExternalListScrollableSpaceExternalListQuery.ts';
import { SitePermissionType } from './graphql/__types__/SpaceExternalListQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const QUERY_PAGE_SIZE = 25;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerWrapper = styled.div({
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RowContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	paddingBottom: token('space.200'),

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:last-child': {
		paddingBottom: 0,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	button: {
		paddingLeft: 0,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'button:hover': {
		textDecoration: 'none',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`a.${PROFILELINK_CLASSNAME}`]: {
		color: token('color.text'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`a.${PROFILELINK_CLASSNAME}:hover`]: {
		color: token('color.text.brand'),
		textDecoration: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserInfoContainer = styled.div({
	display: 'flex',
	flexDirection: 'row',
});

const i18n = defineMessages({
	errorLoadingExternalEntities: {
		id: 'space-guest-list.space-external-list-scrollable.failed-load',
		defaultMessage: 'Failed to load. Please refresh.',
		description:
			'A small error message to show when we fail to load data about the external entities in this space.',
	},
	loadMoreButton: {
		id: 'space-guest-list.space-external-list-scrollable.load-more-button',
		defaultMessage: 'Load more',
		description: 'The label for the button used to load more guests to the space guest list',
	},
});

export function isNonNullable<T extends object | null | undefined>(x: T): x is NonNullable<T> {
	return Boolean(x);
}

export type SpaceExternalListProps = {
	spaceKey: string;
	showGuestLozenge?: boolean;
};

export const SpaceExternalListScrollable: FC<SpaceExternalListProps> = ({
	spaceKey,
	showGuestLozenge = true,
}) => {
	const { data, loading, error, fetchMore } = useQueryInterop<
		SpaceExternalListScrollableSpaceExternalListQuery$data,
		SpaceExternalListScrollableSpaceExternalListQuery$variables
	>(
		graphql`
			query SpaceExternalListScrollableSpaceExternalListQuery(
				$spaceKey: String
				$start: Int
				$limit: Int
			) {
				space(key: $spaceKey) {
					id
					externalCollaboratorCount
					name
					directAccessExternalCollaborators(start: $start, limit: $limit) {
						nodes {
							displayName
							permissionType
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
								email
							}
						}
					}
				}
			}
		`,
		{
			variables: {
				spaceKey,
				start: 0,
				limit: QUERY_PAGE_SIZE,
			},
		},
	);

	if (error) {
		return (
			<ErrorDisplay error={error}>
				<FormattedMessage {...i18n.errorLoadingExternalEntities} />
			</ErrorDisplay>
		);
	}

	const loadMore = () => {
		if (data?.space?.directAccessExternalCollaborators?.nodes) {
			void fetchMore({
				variables: {
					spaceKey,
					start: data.space.directAccessExternalCollaborators.nodes.length,
					limit: QUERY_PAGE_SIZE,
				},
				updateQuery: (prevResults, { fetchMoreResult }) => {
					return !(fetchMoreResult && fetchMoreResult.space)
						? prevResults
						: {
								space: {
									id: fetchMoreResult.space.id,
									externalCollaboratorCount: fetchMoreResult.space.externalCollaboratorCount,
									name: fetchMoreResult.space.name,
									directAccessExternalCollaborators: {
										nodes: [
											...(prevResults.space?.directAccessExternalCollaborators?.nodes || []),
											...(fetchMoreResult.space.directAccessExternalCollaborators?.nodes || []),
										],
										__typename: 'PaginatedPersonList',
									},
									__typename: 'Space',
								},
							};
				},
			});
		}
	};

	const shouldShowLoadMore =
		data?.space?.directAccessExternalCollaborators?.nodes &&
		data?.space?.externalCollaboratorCount >
			data?.space?.directAccessExternalCollaborators?.nodes.length;

	return loading ? (
		<SpinnerWrapper>
			<AkSpinner size="small" testId="space-externallist-scrollable-loading-spinner" />
		</SpinnerWrapper>
	) : (
		<div>
			{(data?.space?.directAccessExternalCollaborators?.nodes ?? [])
				.filter(isNonNullable)
				.map((user, index) => (
					<RowContainer key={(user as KnownUser).accountId || index}>
						<UserInfoContainer>
							<GlobalPermissionsUserRow
								name={user.displayName ?? ''}
								avatarSrc={user.profilePicture?.path}
								lozenge={
									showGuestLozenge && user.permissionType === SitePermissionType.EXTERNAL ? (
										<ExternalCollaboratorUserLozenge />
									) : null
								}
								subText={(user as KnownUser).email}
								accountId={(user as KnownUser).accountId ?? ''}
								permissionType={user.permissionType as SitePermissionType}
								showQuickActionMenu={false}
								useProfileLink
							/>
						</UserInfoContainer>
					</RowContainer>
				))}
			{shouldShowLoadMore && (
				<Button
					testId="load-more-button"
					appearance="default"
					onClick={loadMore}
					spacing="default"
					shouldFitContainer
				>
					<FormattedMessage {...i18n.loadMoreButton} />
				</Button>
			)}
		</div>
	);
};
